"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import { startConversation, startSelfTest } from "@/lib/crypto/conversation-client";
import AgentStatusBadge from "@/components/AgentStatusBadge";
import { PublicListing, PublicPersonalListing } from "@/lib/listings/public";

interface MyListing {
  id: string;
  one_liner: string | null;
}

/**
 * 리스팅 공개 상세 화면의 실제 내용 — SSR 라우트(퍼머링크 /@handle/[slug], 계정 프로필 /@handle,
 * 레거시 UUID /listings/[id])가 전부 이 컴포넌트를 공유한다. 데이터 조회는 서버 컴포넌트가 이미
 * 끝내고 initialListing으로 넘겨주므로, 여기서는 다시 fetch하지 않는다(크롤러가 받는 초기 HTML에
 * 실제 내용이 있어야 한다는 게 이 퍼머링크 기능의 요구사항 자체다 — CSR로 비워두면 안 됨).
 *
 * 로그인 여부·내 리스팅 목록(대화 시작 UI용)·신고 폼은 방문자마다 다른 세션 종속 정보라 여기서
 * 그대로 클라이언트에서 fetch한다 — 크롤러가 못 봐도 상관없는 부분.
 */
export default function ListingDetailView({
  listingId,
  initialListing,
}: {
  listingId: string;
  initialListing: PublicListing | PublicPersonalListing | null;
}) {
  const t = useTranslations("listingDetail");
  const tr = useTranslations("roles");
  const tc = useTranslations("common");
  // 회사 계정은 자가 테스트가 /dashboard/listings/[id] 편집 화면에 있는데, 그 화면은 회사
  // 리스팅 전용 API(is_personal이면 404)라 개인 계정은 쓸 수 없다 — 이 페이지(자기 @handle
  // 프로필)에 인라인으로 넣어 대체한다(대표 지시 2026-09-27). API 자체는 is_personal을 안
  // 가려서 원래도 동작했다 — UI가 없었을 뿐.
  const tEdit = useTranslations("listings.edit");
  const router = useRouter();

  const listing = initialListing;

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [myListings, setMyListings] = useState<MyListing[] | null>(null);
  const [selectedMyListingId, setSelectedMyListingId] = useState<string>("");
  const [openingMessage, setOpeningMessage] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [selfTestDraft, setSelfTestDraft] = useState("");
  const [selfTestBusy, setSelfTestBusy] = useState(false);
  const [selfTestError, setSelfTestError] = useState<string | null>(null);

  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState("false_listing");
  const [reportDescription, setReportDescription] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportResult, setReportResult] = useState<string | null>(null);
  // 로그인 여부 확인이 끝나기 전에 "로그인하세요" CTA가 잠깐 떴다 사라지는 깜빡임을 막는다
  // (로그인된 사용자에게 순간적으로 가입 유도 문구가 보이면 안 됨).
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/me/listings");
        if (cancelled) return;
        // 401(비로그인)이 아니면 로그인 상태 — 개인 계정처럼 리스팅이 0개여도 로그인은 된 것이라
        // 신고하기 같은 "로그인만 필요한" 기능은 그대로 보여야 한다(대화 시작 UI와는 다른 조건).
        if (res.status !== 401) setIsLoggedIn(true);
        if (!res.ok) return; // 비로그인/에러 — 대화 시작 UI는 조용히 숨김(에러 아님)
        const json = await res.json();
        if (!cancelled) {
          setMyListings(json.listings);
          if (json.listings.length > 0) setSelectedMyListingId(json.listings[0].id);
        }
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleStartConversation() {
    if (!selectedMyListingId || !openingMessage.trim()) return;
    setStarting(true);
    setStartError(null);
    try {
      const result = await startConversation(selectedMyListingId, listingId, openingMessage.trim());
      router.push(`/dashboard/conversations/${result.session.id}?listing_id=${selectedMyListingId}`);
    } catch (e) {
      setStartError(e instanceof Error ? e.message : tc("unknownError"));
    } finally {
      setStarting(false);
    }
  }

  async function handleStartSelfTest() {
    if (!selfTestDraft.trim()) return;
    setSelfTestBusy(true);
    setSelfTestError(null);
    try {
      const result = await startSelfTest(listingId, selfTestDraft.trim());
      router.push(`/dashboard/conversations/${result.session.id}?listing_id=${listingId}`);
    } catch (e) {
      setSelfTestError(e instanceof Error ? e.message : tc("unknownError"));
    } finally {
      setSelfTestBusy(false);
    }
  }

  async function handleSubmitReport() {
    setReportSubmitting(true);
    setReportResult(null);
    try {
      const res = await fetch("/api/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_type: "listing", target_id: listingId, category: reportCategory, description: reportDescription.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? tc("unknownError"));
      setReportResult(t("reportSubmitted"));
      setReportDescription("");
    } catch (e) {
      setReportResult(e instanceof Error ? e.message : tc("unknownError"));
    } finally {
      setReportSubmitting(false);
    }
  }

  // 자기 자신에게는 대화를 걸 수 없다 — myListings에 이 리스팅 자신이 포함돼 있으면 "자기 리스팅 보는 중"으로 구분한다
  // (실사용 중 발견: 이 구분이 없어서 자기 리스팅을 봤을 때도 "리스팅을 만드세요" 안내가 잘못 떴었다).
  const otherListings = myListings?.filter((l) => l.id !== listingId) ?? null;
  const isOwnListing = myListings?.some((l) => l.id === listingId) ?? false;
  // A2F 리스팅(0031, 콜드 스타트 대응 — 관리자가 등록한, 아직 에이전트가 없는 기업) — 대화 시작 대신
  // 외부 문의 폼으로 연결한다. 평점·대화 관련 UI는 전부 의미가 없어 숨긴다.
  const isFormKind = !listing?.is_personal && (listing as PublicListing | null)?.listing_kind === "form";

  return (
    <div className="mx-auto flex w-full max-w-xl flex-1 flex-col gap-6 px-6 py-16">
      <Link href="/search" className="text-sm text-zinc-500 underline">
        {tc("back")}
      </Link>

      {!listing ? (
        <p className="text-sm text-red-600">{t("notFound")}</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-50">
              {listing.accounts?.display_name ?? "—"}
            </h1>
            <div className="flex items-center gap-2">
              {listing.is_personal && (
                <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-500 dark:bg-zinc-800">{t("personalBadge")}</span>
              )}
              <AgentStatusBadge
                status={isFormKind ? "form" : listing.agent_status}
                connection={listing.agent_connection}
              />
            </div>
          </div>

          {listing.is_personal ? (
            // 개인 계정의 프로필 — 표시명과 자기소개만 보여준다. 업종/지역/평판은 회사 리스팅에만
            // 의미가 있어 그대로 숨긴다. 대표 지시(2026-09-27): 다만 에이전트 온라인 뱃지와 대화
            // 시작 UI는 개인 계정도 동일하게 노출한다 — 개인도 실제 에이전트를 연결해 응답할 수
            // 있고, 방문자가 여기서 바로 문의할 수 있어야 한다(이전엔 UI에서 전부 숨겨져 있었음).
            <p className="text-base text-zinc-800 dark:text-zinc-200">{listing.one_liner ?? t("personalNoIntro")}</p>
          ) : (
            <>
              {(listing as PublicListing).accounts?.verification_tier === 1 && (listing as PublicListing).accounts?.email_domain ? (
                <p className="text-xs text-green-700 dark:text-green-400">
                  {t("domainVerified", { domain: (listing as PublicListing).accounts!.email_domain! })}
                </p>
              ) : (
                <p className="text-xs text-zinc-500">{t("domainUnverified")}</p>
              )}

              {!isFormKind &&
                ((listing as PublicListing).average_rating !== null ? (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    {"★".repeat(Math.round((listing as PublicListing).average_rating!))}
                    {"☆".repeat(5 - Math.round((listing as PublicListing).average_rating!))}
                    <span className="ml-1 text-zinc-500">
                      {t("ratingSummary", { average: (listing as PublicListing).average_rating!.toFixed(1), count: (listing as PublicListing).rating_count })}
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-zinc-500">{t("noRatingsYet")}</p>
                ))}

              <div className="flex flex-wrap gap-1 text-xs text-zinc-500">
                {(listing as PublicListing).roles.map((role) => (
                  <span key={role} className="rounded bg-zinc-100 px-2 py-0.5 dark:bg-zinc-800">
                    {tr(role)}
                  </span>
                ))}
              </div>

              <p className="text-base text-zinc-800 dark:text-zinc-200">{listing.one_liner}</p>

              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-lg border border-zinc-200 p-4 text-sm dark:border-zinc-800">
                {(listing as PublicListing).categories && (
                  <>
                    <dt className="text-zinc-500">{t("category")}</dt>
                    <dd className="text-zinc-900 dark:text-zinc-50">{(listing as PublicListing).categories!.path}</dd>
                  </>
                )}
                {(listing as PublicListing).regions && (
                  <>
                    <dt className="text-zinc-500">{t("region")}</dt>
                    <dd className="text-zinc-900 dark:text-zinc-50">{(listing as PublicListing).regions!.path}</dd>
                  </>
                )}
                {(listing as PublicListing).collab_interest && (
                  <>
                    <dt className="text-zinc-500">{t("collabInterest")}</dt>
                    <dd className="text-zinc-900 dark:text-zinc-50">{(listing as PublicListing).collab_interest}</dd>
                  </>
                )}
              </dl>
            </>
          )}

          {isFormKind && (listing as PublicListing).contact_url && sessionChecked && isLoggedIn && (
            <div className="flex flex-col items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm dark:border-orange-900 dark:bg-orange-950">
              <p className="text-orange-800 dark:text-orange-300">{t("contactFormNote")}</p>
              <a
                href={(listing as PublicListing).contact_url!}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white dark:bg-zinc-50 dark:text-zinc-900"
              >
                {t("contactButton")}
              </a>
            </div>
          )}

          {isFormKind && sessionChecked && !isLoggedIn && (
            // 대표 지시(2026-09-10): 비로그인 방문자는 A2F든 A2A든 똑같이 가입 유도 CTA를 먼저
            // 보게 한다 — 바로 외부 문의 폼으로 나가버리면 회원가입·리스팅 작성 유도 효과가 없다.
            <div className="flex flex-col items-start gap-3 rounded-lg border border-zinc-900 bg-zinc-50 p-5 text-sm dark:border-zinc-50 dark:bg-zinc-900">
              <p className="font-medium text-zinc-900 dark:text-zinc-50">{t("signupPromptNote")}</p>
              <Link
                href="/login"
                className="rounded-lg bg-zinc-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-zinc-700 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-zinc-200"
              >
                {t("signupPromptCta")}
              </Link>
            </div>
          )}

          {!isFormKind && otherListings && otherListings.length > 0 && (
            <div className="flex flex-col gap-2 rounded-lg border border-zinc-200 p-4 dark:border-zinc-800">
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t("startConversation")}</span>
              {otherListings.length > 1 && (
                <select
                  value={selectedMyListingId}
                  onChange={(e) => setSelectedMyListingId(e.target.value)}
                  className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                >
                  {otherListings.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.one_liner ?? l.id}
                    </option>
                  ))}
                </select>
              )}
              <textarea
                value={openingMessage}
                onChange={(e) => setOpeningMessage(e.target.value)}
                placeholder={t("openingMessagePlaceholder")}
                className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                rows={3}
              />
              {startError && <p className="text-sm text-red-600">{startError}</p>}
              <button
                onClick={handleStartConversation}
                disabled={starting || !openingMessage.trim()}
                className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-zinc-50 dark:text-zinc-900"
              >
                {starting ? tc("saving") : t("startConversation")}
              </button>
            </div>
          )}

          {!isFormKind && isOwnListing && otherListings?.length === 0 && !listing.is_personal && (
            // 실사용 중 발견: 자기 리스팅을 볼 때도 "리스팅을 만드세요" 안내가 잘못 떴었다 —
            // 자기 자신에게는 대화를 걸 수 없는 게 정상이니, 대신 셀프 테스트로 안내한다.
            <div className="flex flex-col gap-2 rounded-lg border border-dashed border-zinc-300 p-4 text-sm dark:border-zinc-700">
              <p className="text-zinc-700 dark:text-zinc-300">{t("ownListingNote")}</p>
              <Link href={`/dashboard/listings/${listingId}`} className="self-start rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white dark:bg-zinc-50 dark:text-zinc-900">
                {t("ownListingCta")}
              </Link>
            </div>
          )}

          {!isFormKind && isOwnListing && otherListings?.length === 0 && listing.is_personal && (
            // 개인 계정은 /dashboard/listings/[id]가 회사 리스팅 전용 API(404 반환)라 그
            // 편집화면의 셀프 테스트를 못 쓴다 — 여기 자기 @handle 프로필에 바로 인라인으로 둔다.
            <div className="flex flex-col gap-2 rounded-lg border border-dashed border-zinc-300 p-4 text-sm dark:border-zinc-700">
              <p className="text-zinc-700 dark:text-zinc-300">{t("ownPersonalListingNote")}</p>
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{tEdit("selfTestTitle")}</span>
              <p className="text-xs text-zinc-500">{tEdit("selfTestHint")}</p>
              <textarea
                value={selfTestDraft}
                onChange={(e) => setSelfTestDraft(e.target.value)}
                placeholder={tEdit("selfTestPlaceholder")}
                className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                rows={3}
              />
              {selfTestError && <p className="text-sm text-red-600">{selfTestError}</p>}
              <button
                onClick={handleStartSelfTest}
                disabled={selfTestBusy || !selfTestDraft.trim()}
                className="self-start rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-zinc-50 dark:text-zinc-900"
              >
                {selfTestBusy ? tc("saving") : tEdit("selfTestButton")}
              </button>
            </div>
          )}

          {!isFormKind && isLoggedIn && !isOwnListing && otherListings?.length === 0 && (
            // 리스팅이 없는 로그인 계정은 대화 시작 UI가 조용히 사라지기만 해서 버그처럼 보였다 —
            // 왜 안 보이는지, 어떻게 하면 되는지 안내한다.
            <div className="flex flex-col gap-2 rounded-lg border border-dashed border-zinc-300 p-4 text-sm dark:border-zinc-700">
              <p className="text-zinc-700 dark:text-zinc-300">{t("needListingNote")}</p>
              <Link href="/quickstart" className="self-start rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white dark:bg-zinc-50 dark:text-zinc-900">
                {t("needListingAgentCta")}
              </Link>
            </div>
          )}

          {!isFormKind && sessionChecked && !isLoggedIn && (
            // 마케팅 요청: 로그인 안 한 방문자에게는 대화 시작 UI 자체가 조용히 사라졌었다 —
            // 3초짜리 OAuth 로그인(=가입)이라는 걸 강조해 눈에 띄는 CTA로 바꾼다.
            // 실사용 중 발견(2026-09-27): 이 블록이 개인 프로필에도 뜨게 됐는데 문구가 "이
            // 회사의"로 고정되어 있어 개인을 볼 때도 어색하게 "회사"라고 나왔다 — 분기한다.
            <div className="flex flex-col items-start gap-3 rounded-lg border border-zinc-900 bg-zinc-50 p-5 text-sm dark:border-zinc-50 dark:bg-zinc-900">
              <p className="font-medium text-zinc-900 dark:text-zinc-50">{t(listing.is_personal ? "signupPromptNotePersonal" : "signupPromptNote")}</p>
              <Link
                href="/login"
                className="rounded-lg bg-zinc-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-zinc-700 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-zinc-200"
              >
                {t("signupPromptCta")}
              </Link>
            </div>
          )}

          {isLoggedIn && (
            <div className="flex flex-col gap-2">
              {!reportOpen ? (
                <button onClick={() => setReportOpen(true)} className="self-start text-xs text-zinc-400 underline hover:text-zinc-600">
                  {t("reportButton")}
                </button>
              ) : (
                <div className="flex flex-col gap-2 rounded-lg border border-zinc-200 p-4 text-sm dark:border-zinc-800">
                  <select
                    value={reportCategory}
                    onChange={(e) => setReportCategory(e.target.value)}
                    className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                  >
                    <option value="impersonation">{t("reportCategory.impersonation")}</option>
                    <option value="false_listing">{t("reportCategory.false_listing")}</option>
                    <option value="spam">{t("reportCategory.spam")}</option>
                    <option value="tag_abuse">{t("reportCategory.tag_abuse")}</option>
                    <option value="other">{t("reportCategory.other")}</option>
                  </select>
                  <textarea
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    placeholder={t("reportDescriptionPlaceholder")}
                    className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                    rows={2}
                  />
                  {reportResult && <p className="text-xs text-zinc-600 dark:text-zinc-400">{reportResult}</p>}
                  <button
                    onClick={handleSubmitReport}
                    disabled={reportSubmitting}
                    className="self-start rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 disabled:opacity-50 dark:border-red-900 dark:text-red-400"
                  >
                    {reportSubmitting ? tc("saving") : t("reportSubmitButton")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
